import { useEffect, useState } from 'react';
import { Modal, Stack, TextInput, Button, Group, Alert, Text } from '@mantine/core';

interface CopyFlowModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  /** One line under the title saying what will (and will not) change. */
  description?: string;
  confirmLabel: string;
  defaultName: string;
  onConfirm: (name: string) => Promise<void>;
}

/**
 * Names the result of a copy: a flow from a template, a template from a flow,
 * or a blank template. Shared so all three behave the same way.
 */
export function CopyFlowModal({
  opened,
  onClose,
  title,
  description,
  confirmLabel,
  defaultName,
  onConfirm,
}: CopyFlowModalProps) {
  const [name, setName] = useState(defaultName);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start fresh each time it opens, so a name typed for one copy never leaks
  // into the next.
  useEffect(() => {
    if (opened) {
      setName(defaultName);
      setError(null);
    }
  }, [opened, defaultName]);

  const trimmed = name.trim();

  const submit = async () => {
    if (!trimmed || pending) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <Stack>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending} disabled={!trimmed}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
